// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

import type { Box, ExportSettings, PageInfo } from './types';
import { boxFromDrag, isMeaningful, nextBoxId } from './redact';
import { GLOSSARY } from './glossary';
import { mountEventTrail } from './eventlog';

export interface UIHandlers {
  onFile: (file: File) => void;
  onSample: () => void;
  onReset: () => void;
  onFind: (term: string) => void;
  onExport: () => void;
  onShare: () => void;
  onBoxesChanged: () => void;
}

export type RenderPageFn = (index: number, canvas: HTMLCanvasElement) => Promise<void>;

export interface UI {
  setDocument: (pages: PageInfo[], render: RenderPageFn) => void;
  getBoxes: () => Map<number, Box[]>;
  addBoxesToPage: (index: number, boxes: Box[]) => number;
  clearAllBoxes: () => void;
  totalBoxes: () => number;
  showStage: (stage: 'empty' | 'working') => void;
  setBusy: (on: boolean, phase?: string, pct?: number) => void;
  setError: (msg: string) => void;
  clearError: () => void;
  toast: (msg: string) => void;
  setFileName: (name: string) => void;
  setShareVisible: (on: boolean) => void;
  getExportSettings: () => ExportSettings;
  setExportSettings: (s: ExportSettings) => void;
  scrollToPage: (index: number) => void;
  closeOverlays: () => boolean;
}

const g = (t: string) =>
  `<span class="glossary-link" data-term="${t}" tabindex="0" role="button">${t}</span>`;

export function createUI(root: HTMLElement, h: UIHandlers): UI {
  root.innerHTML = SHELL;

  const $ = <T extends HTMLElement = HTMLElement>(sel: string) => root.querySelector(sel) as T;
  const stageEmpty = $('#stage-empty');
  const stageWorking = $('#stage-working');
  const dropzone = $('#dropzone');
  const fileInput = $<HTMLInputElement>('#file-input');
  const errorBar = $('#error-bar');
  const errorMsg = $('#error-msg');
  const busy = $('#busy');
  const busyLabel = $('#busy-label');
  const busyBar = $('#busy-bar');
  const pagesEl = $('#pages');
  const fileNameEl = $('#file-name');
  const findInput = $<HTMLInputElement>('#find-input');
  const boxCountEl = $('#box-count');
  const toastEl = $('#toast');
  const modalHost = $('#modal-host');
  const drawer = $('#drawer');
  const drawerTrail = $('#drawer-trail');
  const shareBtn = $<HTMLButtonElement>('#act-share');
  const dpiSel = $<HTMLSelectElement>('#opt-dpi');
  const sharpChk = $<HTMLInputElement>('#opt-sharp');
  const flattenChk = $<HTMLInputElement>('#opt-flatten');
  const modeBtn = $<HTMLButtonElement>('#mode-btn');

  // ── State ──────────────────────────────────────────────────────────────
  const boxesByPage = new Map<number, Box[]>();
  const overlays = new Map<number, HTMLElement>();
  const undoStack: Array<{ page: number; id: string }> = [];
  let drawMode = true;

  // ── Drop zone + file input ────────────────────────────────────────────
  const openPicker = () => fileInput.click();
  dropzone.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('#sample-btn')) return;
    openPicker();
  });
  dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  });
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (f) h.onFile(f);
    fileInput.value = '';
  });
  $('#sample-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    h.onSample();
  });
  $('#new-file').addEventListener('click', () => h.onReset());
  $('#error-dismiss').addEventListener('click', () => errorBar.classList.remove('show'));

  let dragDepth = 0;
  const setDrag = (on: boolean) => dropzone.classList.toggle('dragover', on);
  window.addEventListener('dragenter', (e) => {
    e.preventDefault();
    dragDepth++;
    setDrag(true);
  });
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('dragleave', (e) => {
    e.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setDrag(false);
  });
  window.addEventListener('drop', (e) => {
    e.preventDefault();
    dragDepth = 0;
    setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) h.onFile(f);
  });

  // ── Toolbar actions ────────────────────────────────────────────────────
  const runFind = () => {
    const term = findInput.value.trim();
    if (term) h.onFind(term);
  };
  $('#find-btn').addEventListener('click', runFind);
  findInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runFind();
    }
  });
  $('#undo-btn').addEventListener('click', undoLast);
  $('#clear-btn').addEventListener('click', () => {
    clearAllBoxes();
    toast('Cleared all boxes');
  });
  $('#act-export').addEventListener('click', () => h.onExport());
  shareBtn.addEventListener('click', () => h.onShare());

  modeBtn.addEventListener('click', () => setMode(!drawMode));
  function setMode(draw: boolean) {
    drawMode = draw;
    pagesEl.classList.toggle('draw', draw);
    pagesEl.classList.toggle('move', !draw);
    modeBtn.classList.toggle('active', draw);
    modeBtn.innerHTML = draw ? `${PEN_ICON} Redact` : `${HAND_ICON} Scroll`;
    modeBtn.setAttribute('aria-pressed', String(draw));
  }
  setMode(true);

  // ── Export options ─────────────────────────────────────────────────────
  for (const el of [dpiSel, sharpChk, flattenChk]) {
    el.addEventListener('change', () => h.onBoxesChanged());
  }

  // ── Modals ─────────────────────────────────────────────────────────────
  const openModal = (title: string, bodyHtml: string) => {
    modalHost.innerHTML = `
      <div class="modal-backdrop" data-close></div>
      <div class="modal" role="dialog" aria-modal="true" aria-label="${title}">
        <div class="modal-head">
          <h2>${title}</h2>
          <button class="icon-btn" data-close aria-label="Close">${X_ICON}</button>
        </div>
        <div class="modal-body">${bodyHtml}</div>
      </div>`;
    modalHost.hidden = false;
    modalHost.querySelectorAll('[data-close]').forEach((el) =>
      el.addEventListener('click', closeModal),
    );
    wireGlossary(modalHost);
    (modalHost.querySelector('.icon-btn') as HTMLElement)?.focus();
  };
  const closeModal = () => {
    modalHost.hidden = true;
    modalHost.innerHTML = '';
  };
  $('#nav-how').addEventListener('click', () => openModal('How it works', HOW_HTML));
  $('#nav-privacy').addEventListener('click', () => openModal('Privacy', PRIVACY_HTML));
  $('#nav-about').addEventListener('click', () => openModal('About Redactwell', ABOUT_HTML));
  $('#trust-banner').addEventListener('click', () => openModal('Privacy', PRIVACY_HTML));

  // ── Event drawer ───────────────────────────────────────────────────────
  mountEventTrail(drawerTrail);
  const setDrawer = (open: boolean) => {
    drawer.classList.toggle('open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    $('#drawer-toggle').setAttribute('aria-expanded', String(open));
  };
  $('#drawer-toggle').addEventListener('click', () => setDrawer(!drawer.classList.contains('open')));
  $('#drawer-close').addEventListener('click', () => setDrawer(false));

  // ── Keyboard ───────────────────────────────────────────────────────────
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (closeOverlays()) e.preventDefault();
      return;
    }
    if (
      (e.key === 'Delete' || e.key === 'Backspace') &&
      !stageWorking.hidden &&
      document.activeElement !== findInput
    ) {
      if (undoStack.length) {
        e.preventDefault();
        undoLast();
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !stageWorking.hidden) {
      e.preventDefault();
      undoLast();
    }
  });

  const closeOverlays = (): boolean => {
    if (!modalHost.hidden) {
      closeModal();
      return true;
    }
    if (drawer.classList.contains('open')) {
      setDrawer(false);
      return true;
    }
    const tip = document.getElementById('glossary-tip');
    if (tip && tip.classList.contains('show')) {
      tip.classList.remove('show');
      return true;
    }
    return false;
  };

  wireGlossary(root);

  // ── Box helpers ────────────────────────────────────────────────────────
  function updateCount() {
    const n = totalBoxes();
    boxCountEl.textContent = String(n);
    boxCountEl.classList.toggle('has', n > 0);
    h.onBoxesChanged();
  }

  function totalBoxes(): number {
    let n = 0;
    for (const list of boxesByPage.values()) n += list.filter((b) => isMeaningful(b)).length;
    return n;
  }

  function addBox(page: number, box: Box) {
    if (!isMeaningful(box)) return;
    const list = boxesByPage.get(page) ?? [];
    list.push(box);
    boxesByPage.set(page, list);
    undoStack.push({ page, id: box.id });
    renderBoxEl(page, box);
    updateCount();
  }

  function removeBox(page: number, id: string) {
    const list = boxesByPage.get(page);
    if (!list) return;
    const i = list.findIndex((b) => b.id === id);
    if (i >= 0) list.splice(i, 1);
    const idx = undoStack.findIndex((u) => u.id === id);
    if (idx >= 0) undoStack.splice(idx, 1);
    overlays.get(page)?.querySelector(`[data-box="${id}"]`)?.remove();
    updateCount();
  }

  function undoLast() {
    const last = undoStack.pop();
    if (!last) return;
    removeBox(last.page, last.id);
  }

  function clearAllBoxes() {
    boxesByPage.clear();
    undoStack.length = 0;
    for (const ov of overlays.values()) ov.querySelectorAll('.rbox').forEach((b) => b.remove());
    updateCount();
  }

  function renderBoxEl(page: number, box: Box) {
    const ov = overlays.get(page);
    if (!ov) return;
    const el = document.createElement('div');
    el.className = 'rbox';
    el.dataset.box = box.id;
    el.style.left = `${box.x * 100}%`;
    el.style.top = `${box.y * 100}%`;
    el.style.width = `${box.w * 100}%`;
    el.style.height = `${box.h * 100}%`;
    const del = document.createElement('button');
    del.type = 'button';
    del.className = 'rbox-del';
    del.setAttribute('aria-label', 'Remove this redaction');
    del.innerHTML = X_ICON_SM;
    del.addEventListener('pointerdown', (e) => e.stopPropagation());
    del.addEventListener('click', (e) => {
      e.stopPropagation();
      removeBox(page, box.id);
    });
    el.appendChild(del);
    ov.appendChild(el);
  }

  // ── Drawing on an overlay ──────────────────────────────────────────────
  function wireOverlay(page: number, ov: HTMLElement) {
    let draft: HTMLElement | null = null;
    let sx = 0;
    let sy = 0;
    let rect: DOMRect | null = null;

    ov.addEventListener('pointerdown', (e) => {
      if (!drawMode) return;
      if (e.target !== ov) return; // clicked an existing box/handle
      e.preventDefault();
      rect = ov.getBoundingClientRect();
      sx = e.clientX - rect.left;
      sy = e.clientY - rect.top;
      draft = document.createElement('div');
      draft.className = 'rbox draft';
      ov.appendChild(draft);
      ov.setPointerCapture(e.pointerId);
    });
    ov.addEventListener('pointermove', (e) => {
      if (!draft || !rect) return;
      const cx = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const cy = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
      draft.style.left = `${Math.min(sx, cx)}px`;
      draft.style.top = `${Math.min(sy, cy)}px`;
      draft.style.width = `${Math.abs(cx - sx)}px`;
      draft.style.height = `${Math.abs(cy - sy)}px`;
    });
    const finish = (e: PointerEvent) => {
      if (!draft || !rect) return;
      const cx = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const cy = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
      const box = boxFromDrag(sx, sy, cx, cy, rect.width, rect.height);
      draft.remove();
      draft = null;
      rect = null;
      try {
        ov.releasePointerCapture(e.pointerId);
      } catch {
        /* not captured */
      }
      addBox(page, box);
    };
    ov.addEventListener('pointerup', finish);
    ov.addEventListener('pointercancel', () => {
      draft?.remove();
      draft = null;
      rect = null;
    });
  }

  // ── Public: build the page viewer ──────────────────────────────────────
  function setDocument(pages: PageInfo[], render: RenderPageFn) {
    boxesByPage.clear();
    overlays.clear();
    undoStack.length = 0;
    pagesEl.innerHTML = '';
    updateCount();

    const io = new IntersectionObserver(
      (entries) => {
        for (const ent of entries) {
          if (!ent.isIntersecting) continue;
          const wrap = ent.target as HTMLElement;
          if (wrap.dataset.rendered) continue;
          wrap.dataset.rendered = '1';
          const index = Number(wrap.dataset.index);
          const canvas = wrap.querySelector('canvas') as HTMLCanvasElement;
          render(index, canvas).catch(() => {
            wrap.classList.add('render-failed');
          });
          io.unobserve(wrap);
        }
      },
      { rootMargin: '600px 0px' },
    );

    for (const p of pages) {
      const wrap = document.createElement('div');
      wrap.className = 'page';
      wrap.dataset.index = String(p.index);
      wrap.style.aspectRatio = `${p.width} / ${p.height}`;

      const canvas = document.createElement('canvas');
      canvas.className = 'page-canvas';
      const overlay = document.createElement('div');
      overlay.className = 'overlay';
      const num = document.createElement('span');
      num.className = 'page-num';
      num.textContent = `${p.index + 1}`;

      wrap.append(canvas, overlay, num);
      pagesEl.appendChild(wrap);
      overlays.set(p.index, overlay);
      wireOverlay(p.index, overlay);
      io.observe(wrap);
    }
  }

  function addBoxesToPage(index: number, boxes: Box[]): number {
    let added = 0;
    for (const b of boxes) {
      if (!isMeaningful(b)) continue;
      addBox(index, { ...b, id: b.id || nextBoxId() });
      added++;
    }
    return added;
  }

  function scrollToPage(index: number) {
    const wrap = pagesEl.querySelector(`.page[data-index="${index}"]`);
    wrap?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function toast(msg: string) {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    window.clearTimeout((toast as unknown as { t?: number }).t);
    (toast as unknown as { t?: number }).t = window.setTimeout(
      () => toastEl.classList.remove('show'),
      2400,
    );
  }

  return {
    setDocument,
    getBoxes: () => boxesByPage,
    addBoxesToPage,
    clearAllBoxes,
    totalBoxes,
    showStage(stage) {
      stageEmpty.hidden = stage !== 'empty';
      stageWorking.hidden = stage !== 'working';
    },
    setBusy(on, phase, pct) {
      busy.classList.toggle('show', on);
      busy.setAttribute('aria-hidden', String(!on));
      if (phase) busyLabel.textContent = phase;
      const indeterminate = pct == null;
      busyBar.classList.toggle('indeterminate', indeterminate);
      busyBar.style.setProperty('--pct', `${pct ?? 0}%`);
    },
    setError(msg) {
      errorMsg.textContent = msg;
      errorBar.classList.add('show');
    },
    clearError() {
      errorBar.classList.remove('show');
    },
    toast,
    setFileName(name) {
      fileNameEl.textContent = name;
    },
    setShareVisible(on) {
      shareBtn.hidden = !on;
    },
    getExportSettings() {
      return {
        dpi: parseInt(dpiSel.value, 10) || 150,
        sharp: sharpChk.checked,
        flattenAll: flattenChk.checked,
      };
    },
    setExportSettings(s) {
      dpiSel.value = String(s.dpi);
      sharpChk.checked = s.sharp;
      flattenChk.checked = s.flattenAll;
    },
    scrollToPage,
    closeOverlays,
  };
}

// ── Glossary tooltip wiring (shared) ─────────────────────────────────────
function wireGlossary(scope: HTMLElement): void {
  let tip = document.getElementById('glossary-tip');
  if (!tip) {
    tip = document.createElement('div');
    tip.id = 'glossary-tip';
    document.body.appendChild(tip);
    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.glossary-link') && tip) tip.classList.remove('show');
    });
  }
  const show = (el: HTMLElement) => {
    const term = (el.dataset.term || '').toLowerCase();
    const def = GLOSSARY[term];
    if (!def || !tip) return;
    tip.textContent = def;
    const r = el.getBoundingClientRect();
    tip.classList.add('show');
    const tw = Math.min(300, window.innerWidth - 24);
    tip.style.width = `${tw}px`;
    let left = r.left + r.width / 2 - tw / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
    tip.style.left = `${left}px`;
    tip.style.top = `${r.bottom + 8}px`;
  };
  scope.querySelectorAll<HTMLElement>('.glossary-link').forEach((el) => {
    if (el.dataset.wired) return;
    el.dataset.wired = '1';
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      show(el);
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        show(el);
      }
    });
  });
}

// ── Icons ──────────────────────────────────────────────────────────────
const X_ICON = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;
const X_ICON_SM = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;
const PEN_ICON = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`;
const HAND_ICON = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11V6a2 2 0 0 0-4 0M14 10V4a2 2 0 0 0-4 0v2M10 10.5V6a2 2 0 0 0-4 0v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/></svg>`;

const LOGO = `<svg viewBox="0 0 64 64" width="26" height="26" aria-hidden="true"><rect width="64" height="64" rx="14" fill="var(--accent-primary)"/><rect x="15" y="10" width="34" height="44" rx="4" fill="#fff"/><rect x="21" y="18" width="22" height="3.2" rx="1.6" fill="#c7d2fe"/><rect x="21" y="25" width="22" height="6.4" rx="1.6" fill="#111827"/><rect x="21" y="35" width="22" height="3.2" rx="1.6" fill="#c7d2fe"/><rect x="21" y="42" width="14" height="6.4" rx="1.6" fill="#111827"/></svg>`;

// ── Static markup ────────────────────────────────────────────────────────
const SHELL = /* html */ `
<header class="topbar">
  <div class="brand">${LOGO}<span>Redactwell</span></div>
  <nav class="nav">
    <button id="nav-how" class="nav-btn" type="button">How it works</button>
    <button id="nav-privacy" class="nav-btn" type="button">Privacy</button>
    <button id="nav-about" class="nav-btn" type="button">About</button>
    <button id="drawer-toggle" class="nav-btn ghost" type="button" aria-expanded="false" aria-controls="drawer">
      Log <span class="badge"><b id="ev-count">0</b></span>
    </button>
  </nav>
</header>

<main class="main-content">
  <button id="trust-banner" class="trust-banner" type="button">
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    Runs entirely in your browser. Your document never leaves your device.
  </button>

  <div id="error-bar" class="error-bar">
    <span id="error-msg"></span>
    <button id="error-dismiss" class="icon-btn" type="button" aria-label="Dismiss">${X_ICON}</button>
  </div>

  <!-- EMPTY STAGE -->
  <section id="stage-empty" class="stage-empty">
    <div id="dropzone" class="dropzone" tabindex="0" role="button" aria-label="Choose a PDF to redact">
      <div class="dz-icon"><svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><rect x="7.5" y="13" width="9" height="3.4" rx="0.6" fill="currentColor" stroke="none"/></svg></div>
      <div class="dz-title">Drop a PDF, or click to choose</div>
      <div class="dz-sub">Black out anything you like — the covered text is <b>permanently removed</b>, not just hidden. Nothing is uploaded.</div>
      <button id="sample-btn" class="pill" type="button">Try a sample</button>
    </div>
  </section>

  <!-- WORKING STAGE -->
  <section id="stage-working" class="stage-working" hidden>
    <div class="toolbar">
      <div class="tb-file">
        <span id="file-name" class="file-name">document.pdf</span>
        <button id="new-file" class="text-btn" type="button">Choose another</button>
      </div>
      <div class="tb-tools">
        <button id="mode-btn" class="chip toggle" type="button" aria-pressed="true" title="Toggle drawing vs scrolling"></button>
        <div class="find">
          <input id="find-input" class="find-input" type="text" placeholder="Find text to redact…" aria-label="Find text to redact" autocomplete="off" spellcheck="false" />
          <button id="find-btn" class="chip" type="button">Find &amp; redact</button>
        </div>
        <button id="undo-btn" class="chip" type="button" title="Undo last box (⌘/Ctrl+Z)">Undo</button>
        <button id="clear-btn" class="chip" type="button">Clear</button>
        <span class="box-count">Boxes <b id="box-count" class="count-badge">0</b></span>
      </div>
    </div>

    <div class="work-body">
      <div id="pages" class="pages draw"></div>

      <aside class="side">
        <div class="panel">
          <div class="panel-title">Redact</div>
          <p class="hint">In <b>Redact</b> mode, drag a rectangle over anything to black it out. Use <b>Find &amp; redact</b> to place boxes over every match of a word. Switch to <b>Scroll</b> to move around on a phone.</p>
        </div>

        <div class="panel">
          <div class="panel-title">Export</div>
          <div class="opt">
            <label for="opt-dpi">${g('dpi')}</label>
            <select id="opt-dpi" class="select" aria-label="Resolution">
              <option value="150">150 — standard</option>
              <option value="200">200 — high</option>
              <option value="300">300 — very high</option>
            </select>
          </div>
          <label class="check"><input type="checkbox" id="opt-sharp"><span>Sharper pages (PNG, larger files)</span></label>
          <label class="check"><input type="checkbox" id="opt-flatten"><span>${g('flatten')} <b>every</b> page (remove all selectable text)</span></label>
          <div class="actions">
            <button id="act-export" class="btn primary" type="button">Redact &amp; download PDF</button>
            <button id="act-share" class="btn" type="button" hidden>Share redacted PDF</button>
          </div>
          <p class="fineprint">Only pages you redact are ${g('flatten')}ed to an image, so their ${g('text layer')} is destroyed. Untouched pages keep their original text unless you tick “every page”.</p>
        </div>
      </aside>
    </div>

    <div id="busy" class="busy" aria-hidden="true">
      <div class="busy-inner">
        <div id="busy-bar" class="busy-bar"><span></span></div>
        <div id="busy-label" class="busy-label">Working…</div>
      </div>
    </div>
  </section>
</main>

<footer class="site-footer">
  <div class="footer-inner">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
    · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
  </div>
</footer>

<aside id="drawer" class="drawer" aria-hidden="true" aria-label="Event log">
  <div class="drawer-head">
    <div class="drawer-title">Event log</div>
    <button id="drawer-close" class="icon-btn" type="button" aria-label="Close event log">${X_ICON}</button>
  </div>
  <div id="drawer-trail" class="drawer-trail"></div>
</aside>

<input id="file-input" type="file" accept="application/pdf,.pdf" hidden />
<div id="modal-host" hidden></div>
<div id="toast" class="toast" role="status" aria-live="polite"></div>
`;

const HOW_HTML = /* html */ `
<p>Redactwell removes sensitive text from a PDF <b>for real</b> — the black bars are ${g('burned in')}, not layered on top where someone could peel them away. Everything happens inside your browser tab.</p>
<ol class="steps">
  <li><b>Open your PDF.</b> It is parsed and drawn page-by-page on your device. No upload, no account.</li>
  <li><b>Mark what to remove.</b> Drag a rectangle over any name, number or block. Or type a word into <b>${g('find & redact')}</b> and Redactwell drops a box over every match for you to check.</li>
  <li><b>Export.</b> Every page you touched is rasterised at your chosen ${g('dpi')} with the black bars painted into the pixels, then ${g('flatten')}ed into a fresh PDF. The ${g('text layer')} under each bar is gone — you cannot select or copy it back.</li>
  <li><b>Clean file out.</b> ${g('metadata')} (author, title, producer) is dropped, and you download or share the redacted PDF.</li>
</ol>
<p class="muted">Because a redacted page becomes an image, it also loses selectable text elsewhere on that page. Pages you never touch keep their original text — unless you tick “flatten every page”.</p>
`;

const PRIVACY_HTML = /* html */ `
<div class="tm">
  <div class="tm-block ok">
    <h3>Protected</h3>
    <ul>
      <li>Your PDF is opened, rendered, redacted and rebuilt entirely on your device. It is <b>never uploaded</b>.</li>
      <li>Redacted pages are ${g('flatten')}ed to images, so the text under a black bar is <b>physically removed</b> from the output — not merely hidden.</li>
      <li>Document ${g('metadata')} (author, title, producer) is dropped from the file you download.</li>
      <li>Works offline once cached. No cookies, no fingerprinting, no third-party fonts.</li>
    </ul>
  </div>
  <div class="tm-block warn">
    <h3>Not protected</h3>
    <ul>
      <li>The initial page load and static assets are served by GitHub Pages, whose CDN sees the request for the <i>page</i> — never your document.</li>
      <li>Pages you do <i>not</i> redact keep their original selectable ${g('text layer')} (for quality). Tick “flatten every page” to strip all text from the whole file.</li>
      <li>Anything you export (download / share) is then in your hands and out of Redactwell's control.</li>
    </ul>
  </div>
  <div class="tm-block trust">
    <h3>Trust surface</h3>
    <ul>
      <li>The static site bundle (hash-pinned by the GitHub Pages deploy).</li>
      <li>The TLS connection between you and GitHub Pages.</li>
      <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your document is never sent to it.</li>
      <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your files and data never are.</li>
    </ul>
  </div>
</div>
`;

const ABOUT_HTML = /* html */ `
<p><b>Redactwell</b> is a free, private PDF redactor. Black out names, addresses, account numbers or whole paragraphs, and get back a PDF where the covered text is <b>really gone</b> — safe to email, file or publish.</p>
<p>It exists because most “redaction” tools online do the dangerous thing: they draw a black rectangle on top of the ${g('text layer')}, which anyone can select and copy straight out from underneath — and they upload your sensitive document to a server to do it. Redactwell does neither.</p>
<p>Made by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>. Part of a small lab of privacy-first browser tools — see them all at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>
<p class="muted">Rendering uses Mozilla's pdf.js; the output PDF is rebuilt with pdf-lib. Source: <a href="https://github.com/ben-gy/redactwell" target="_blank" rel="noopener">github.com/ben-gy/redactwell</a>.</p>
<p class="muted">Shortcuts: <kbd>Esc</kbd> closes dialogs · <kbd>⌘/Ctrl</kbd>+<kbd>Z</kbd> or <kbd>Delete</kbd> undoes the last box · drag a PDF anywhere onto the page.</p>
`;
